import React, {useCallback, useEffect, useRef} from 'react';
import {observer} from 'mobx-react';
import {CyClass} from '../../../../../common/cy-classes';
import {IVideoPlayer} from '../../../../services/video-live-stream/video-players/video-player.interface';
import {VideoBox} from './boxes/video.box';
import {VideoPlayButtonBox} from "../../../video-player/play-button/boxes/video-play-button.box";
import {GameInfo} from "../../../../services/games/game-info";
import {VideoContainerBox} from '../video-container/video-container.box';
import {IVideoPlayingService} from "../../../../services/video-live-stream/video-playing.service.interface";
import {useServices} from "../../../../react-hooks/use-services.hook";
import {IReactionDisposer, reaction} from "mobx";
import classNames from 'classnames';

interface VideoComponentProps {
    className?: string;
    gameInfo: GameInfo;
    muted: boolean;
    onError?: (hasError: boolean, retryFn?: (() => void) | undefined) => void;
    onLoading?: (isLoading: boolean) => void;
    onWatchTimeLimitReached?: (watchTimeLimitReached: boolean) => void;
    isInCenterStage: boolean;
    inViewport: boolean;
    display: boolean;
    videoPlayingService: IVideoPlayingService;
    isLastGameType?: boolean;
    loop?: boolean;
    isVideoOnDemand?: boolean;
}

export const VideoComponent: React.FC<VideoComponentProps> = observer((props) => {
    const services = useServices();
    const videoPlayerRef = useRef<IVideoPlayer | null>(null);
    const videoElementRef = useRef<HTMLVideoElement | null>(null);
    const reactionsRef = useRef<IReactionDisposer[]>([]);
    const isAppVisible = props.videoPlayingService.isAppVisible;

    const createVideoPlayer = useCallback(() => {
        if(videoElementRef.current) {
            return props.videoPlayingService.getVideoPlayer(props.gameInfo, videoElementRef.current, props.isLastGameType);
        } else {
            return null;
        }

    }, [props.gameInfo, props.isLastGameType, props.videoPlayingService]);

    useEffect(() => {

        const videoPlayer = createVideoPlayer();
        videoPlayerRef.current = videoPlayer;

        const videoElement = videoElementRef.current;

        return () => {
            videoPlayer?.destroy();
            try {
                if(videoElement) {
                    videoElement.poster = "";
                    videoElement.src = "";
                    videoElement.load();
                }
                videoElementRef.current = null;
            }
            catch (err){
                services.logger.error('VideoComponent - Failed to cleanup video element', err);
            }
        }

    }, [createVideoPlayer, services.logger]);


    const loadVideo = useCallback(() => {
        videoPlayerRef.current?.load();
    }, []);

    const playVideo = useCallback(() => {
        videoPlayerRef.current?.play();
    }, []);

    const pauseVideo = useCallback(() => {
        videoPlayerRef.current?.pause();
    }, []);

    const retryPlay = useCallback(() => {
        videoPlayerRef.current?.destroy();
        videoPlayerRef.current = createVideoPlayer();
        playVideo();
    }, [createVideoPlayer, playVideo]);



    useEffect(() => {

        const reactions = reactionsRef.current;

        reactions.push(reaction(() => videoPlayerRef.current?.hasError, (hasError) => {
            props.onError(hasError, hasError ? retryPlay : undefined);
        }));

        reactions.push(reaction(() => videoPlayerRef.current?.isLoading, (isLoading) => {
            props.onLoading(isLoading);
        }));

        reactions.push(reaction(() => videoPlayerRef.current?.watchTimeLimitReached, (watchTimeLimitReached) => {
            props.onWatchTimeLimitReached(watchTimeLimitReached);
        }));

        return () => {
            reactions.forEach(r => r());
            reactionsRef.current = [];
        }

    }, [props, retryPlay]);

    useEffect(() => {

        if(isAppVisible) {
            if (props.isInCenterStage && props.inViewport) {
                playVideo();
            } else if (props.isVideoOnDemand) {
                loadVideo();
            } else {
                pauseVideo();
            }
        } else {
            pauseVideo();
        }
    });

    const handlePlayVideoClick = () => {
        if (isAppVisible && props.isInCenterStage && props.inViewport) {
            playVideo();
            videoPlayerRef.current?.logUIAction('info', 'VideoPlayClick');
            services.analytics.sendAnalytics(props.videoPlayingService.gaCategory, "AUTOPLAY", "VIDEO_PLAY_CLICK", props.gameInfo.gameType, true);
        }
    }


    const renderPlayerControls = () => {
        if(videoPlayerRef.current?.autoplayAllowed || !videoElementRef.current?.paused) {
            return null;
        } else {
            return (<VideoPlayButtonBox onClick={handlePlayVideoClick} />)
        }
    }

    return (
        <VideoContainerBox
            className={classNames(CyClass('cy-live-video-item'), props.className)}
            videoIsLoading={Boolean(videoPlayerRef.current?.isLoading)}
            videoWatchTimeLimitReached={Boolean(videoPlayerRef.current?.watchTimeLimitReached)}
            displayVideo={props.display}
        >
            <VideoBox
                ref={videoElementRef}
                playsInline={true}
                muted={props.muted}
                loop={props.loop}
            />
            {renderPlayerControls()}
        </VideoContainerBox>
    )

});

